import { useMemo } from 'react';
import { CustomTableConfig } from 'commons/components';
import { FieldConfig } from 'commons/components/table/custom-table.type';
import { useTranslation } from 'core/i18n/i18n';
import { EditableColumn } from 'modules/global/model';
import { OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notifications.constants';
import { OperatorCustomNotificationRow } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notifications.model';
import { TrackedEventsByModelCell } from 'modules/settings/pods/settings-operator-custom-notifications/components/tracked-events-by-model-cell';

type TranslateFunction = (key: string) => string;

interface ColumnSpec {
    name: string;
    label: string;
    sortable: boolean;
    format?: (row: OperatorCustomNotificationRow) => React.ReactNode;
}

const columnHeaderKey = (column: string) => `${OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX}.list.columns.${column}`;

const statusKey = (row: OperatorCustomNotificationRow) =>
    `${OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX}.list.status.${row.active ? 'active' : 'inactive'}`;

const buildColumnSpecs = (t: TranslateFunction): ColumnSpec[] => [
    { name: 'notificationName', label: t(columnHeaderKey('name')), sortable: true },
    {
        name: 'trackedEvents',
        label: t(columnHeaderKey('events')),
        sortable: false,
        format: (row) => <TrackedEventsByModelCell groups={row.trackedEvents} />,
    },
    { name: 'monitoringPeriodDays', label: t(columnHeaderKey('period')), sortable: false },
    { name: 'notificationType', label: t(columnHeaderKey('notifications')), sortable: false },
    { name: 'optionalSettings', label: t(columnHeaderKey('optional')), sortable: false },
    {
        name: 'status',
        label: t(columnHeaderKey('status')),
        sortable: false,
        format: (row) => t(statusKey(row)),
    },
];

const toField = (spec: ColumnSpec): FieldConfig<OperatorCustomNotificationRow> => ({
    name: spec.name,
    headerOptions: {
        text: spec.label,
        ...(spec.sortable ? {} : { sortable: false }),
    },
    ...(spec.format && { rowOptions: { cellOptions: { format: spec.format } } }),
});

const toColumnToShow = (spec: ColumnSpec, columnIndex: number): EditableColumn => ({
    field: spec.name,
    text: spec.label,
    visible: true,
    pinned: false,
    columnIndex,
});

export const buildOperatorCustomNotificationsTableConfig = (
    t: TranslateFunction
): CustomTableConfig<OperatorCustomNotificationRow> => {
    const specs = buildColumnSpecs(t);

    return {
        entity: 'operatorCustomNotifications',
        columnsToShow: specs.map((spec, columnIndex) => toColumnToShow(spec, columnIndex)),
        fields: specs.map((spec) => toField(spec)),
    };
};

export const useOperatorCustomNotificationsTableConfig = () => {
    const { t } = useTranslation();

    return useMemo<CustomTableConfig<OperatorCustomNotificationRow>>(
        () => buildOperatorCustomNotificationsTableConfig(t),
        [t]
    );
};
